<template>
  <div
    :class="[
      bem.b(),
      disabled ? bem.is('disabled') : '',
      bem.is(isGray ? 'gray-number-range' : ''),
    ]"
  >
    <!-- 左边InputNumber -->
    <div :class="bem.m('input')">
      <ll-input-number
        v-model:value="minValue"
        :placeholder="startPlaceholder || placeholder[0]"
        :precision="props.precision"
        :min="getMin"
        :max="getMax"
        :disabled="props.disabled"
        :controls="false"
        :bordered="false"
        :step="props.step"
        :size="size"
        style="width: 100%"
        @change="handleMinChange"
        @blur="handleMinBlur"
      />
    </div>

    <!-- 间隔符 -->
    <span :class="bem.m('separator')">
      <slot v-if="$slots.separator" name="separator" />
      <template v-else>{{ separator || '-' }}</template>
    </span>

    <!-- 右边InputNumber -->
    <div :class="bem.m('input')">
      <ll-input-number
        v-model:value="maxValue"
        :placeholder="endPlaceholder || placeholder[1]"
        :precision="props.precision"
        :min="getMin"
        :max="getMax"
        :disabled="props.disabled"
        :controls="false"
        :bordered="false"
        :step="props.step"
        :size="size"
        style="width: 100%"
        @change="handleMaxChange"
        @blur="handleMaxBlur"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { Form } from 'ant-design-vue'
import { isNil } from 'lodash-es'
import { computed, ref, watch } from 'vue'
import { numberRangeEmits, numberRangeProps } from './number-range'

import { LlInputNumber } from '@ll-plus/components'

import type { InputNumberProps } from 'ant-design-vue'

defineOptions({ name: 'LlNumberRange' })

// 1.props/ref
const props = defineProps(numberRangeProps)
// 2.emits
const emits = defineEmits(numberRangeEmits)

const bem = createNamespace('number-range')

const formItemContext = Form.useInjectFormItemContext()

const minValue = ref<InputNumberProps['value']>(undefined)

const maxValue = ref<InputNumberProps['value']>(undefined)

const getMin = computed(() =>
  typeof props.minMaxRange[0] === 'number' && !isNaN(props.minMaxRange[0])
    ? props.minMaxRange[0]
    : props.min,
)
const getMax = computed(() =>
  typeof props.minMaxRange[1] === 'number' && !isNaN(props.minMaxRange[1])
    ? props.minMaxRange[1]
    : props.max,
)

const setValue = () => {
  const arr = [minValue.value, maxValue.value]
  emits('update:value', arr)
  emits('change', arr)
  formItemContext.onFieldChange()
}

const handleMinChange = (e) => {
  if (!isNil(e)) {
    minValue.value = e
  } else {
    minValue.value = undefined
  }
  setValue()
}
const handleMaxChange = (e) => {
  if (!isNil(e)) {
    maxValue.value = e
  } else {
    maxValue.value = undefined
  }
  setValue()
}

// 最小值失去焦点
const handleMinBlur = () => {
  if (props.calibrateSize) {
    if (maxValue.value || maxValue.value === 0) {
      if (minValue.value && minValue.value > maxValue.value) {
        minValue.value = maxValue.value
      }
    }
  }
  setValue()
  formItemContext.onFieldBlur()
}

// 最大值失去焦点
const handleMaxBlur = () => {
  if (props.calibrateSize) {
    if (minValue.value || minValue.value === 0) {
      if (maxValue.value && maxValue.value < minValue.value) {
        maxValue.value = minValue.value
      }
    }
  }
  setValue()
  formItemContext.onFieldBlur()
}

watch(
  () => props.value,
  (n) => {
    if (n && n.length!) {
      if (!isNil(n[0])) {
        minValue.value = n[0]
      } else {
        minValue.value = undefined
      }
      if (!isNil(n[1])) {
        maxValue.value = n[1]
      } else {
        maxValue.value = undefined
      }
    } else {
      minValue.value = undefined
      maxValue.value = undefined
    }
  },
  {
    immediate: true,
    deep: true,
  },
)
</script>
